import { Auth, Hub } from 'aws-amplify';
import { useCallback, useEffect, useRef, useState } from 'react';

import AppRouter from '../navigation/AppRouter';

import { LoopConfirmSignUp, LoopForgotPassword, LoopSignIn, LoopSignUp, LoopVerifyContact } from '.';

export type AuthStep =
  | 'loading'
  | 'signIn'
  | 'signUp'
  | 'confirmSignUp'
  | 'confirmSignIn'
  | 'forgotPassword'
  | 'verifyContact'
  | 'requireNewPassword'
  | 'signedIn';

const AUTH_STEPS: readonly AuthStep[] = [
  'loading',
  'signIn',
  'signUp',
  'confirmSignUp',
  'confirmSignIn',
  'forgotPassword',
  'verifyContact',
  'requireNewPassword',
  'signedIn',
];

const isAuthStep = (state: string): state is AuthStep => (AUTH_STEPS as string[]).includes(state);

/**
 * Local replacement for aws-amplify-react's withAuthenticator/Authenticator: tracks the
 * current auth step and renders the matching screen, using @aws-amplify/auth directly.
 *
 * confirmSignIn (MFA) and requireNewPassword (admin-created temp password) are not reachable in this app:
 * no code path enables MFA or creates users with a temporary password, so no screen renders for those steps.
 */
const AuthController: React.FC = () => {
  const [authState, setAuthState] = useState<AuthStep>('loading');
  const [authData, setAuthData] = useState<unknown>(null);
  // Generation counter: bumped whenever a signOut happens, so a verifiedContact check that was
  // already in flight can detect it's stale and not overwrite the newer signed-out state.
  const verificationRequestId = useRef(0);

  const changeState = useCallback((state: string, data?: unknown) => {
    if (!isAuthStep(state)) {
      // Guard against unsupported/unknown states (e.g. unhandled Cognito challenges) rendering a blank page.
      console.error(`Unsupported auth state "${state}", falling back to signIn`);
      setAuthState('signIn');
      setAuthData(null);
      return;
    }
    setAuthState(state);
    setAuthData(data ?? null);
  }, []);

  // Shared by both session restoration on mount and the Hub 'signIn' event so a page refresh
  // can't bypass the contact-verification gate that a fresh sign-in goes through.
  const resolveVerifiedContact = useCallback(
    async (user: unknown) => {
      const requestId = (verificationRequestId.current += 1);

      try {
        const result = (await Auth.verifiedContact(user)) as { verified: Record<string, unknown> };
        if (requestId !== verificationRequestId.current) return; // superseded by a later auth event

        if (Object.keys(result.verified).length > 0) {
          changeState('signedIn', user);
        } else {
          changeState('verifyContact', Object.assign(user as Record<string, unknown>, result));
        }
      } catch (err) {
        if (requestId !== verificationRequestId.current) return;

        // Fail closed: an error checking verification status must not grant access.
        console.error(err);
        try {
          await Auth.signOut();
        } catch {
          // best-effort sign out
        }
        changeState('signIn');
      }
    },
    [changeState]
  );

  useEffect(() => {
    let isMounted = true;

    Auth.currentAuthenticatedUser()
      .then((user: unknown) => {
        if (isMounted) void resolveVerifiedContact(user);
      })
      .catch(() => {
        if (isMounted) changeState('signIn');
      });

    const unsubscribe = Hub.listen('auth', ({ payload }: { payload: { event: string; data?: unknown } }) => {
      if (payload.event === 'signIn') {
        void resolveVerifiedContact(payload.data);
      } else if (payload.event === 'signOut') {
        verificationRequestId.current += 1;
        changeState('signIn');
      }
    });

    return () => {
      isMounted = false;
      // Invalidate any verifiedContact check still in flight (e.g. Strict Mode's mount->unmount->
      // mount) so it can't resolve later and overwrite state established by a subsequent effect run.
      verificationRequestId.current += 1;
      unsubscribe();
    };
  }, [changeState, resolveVerifiedContact]);

  if (authState === 'loading') {
    return null;
  }

  if (authState === 'signedIn') {
    return <AppRouter />;
  }

  const screenProps = { authState, authData, onStateChange: changeState };

  return (
    <>
      <LoopSignIn {...screenProps} />
      <LoopVerifyContact {...screenProps} />
      <LoopSignUp {...screenProps} />
      <LoopConfirmSignUp {...screenProps} />
      <LoopForgotPassword {...screenProps} />
    </>
  );
};

export default AuthController;
